import {
  FilePdfIcon,
  FileTextIcon,
  NewspaperClippingIcon,
  PlusIcon,
  ShoppingBagIcon,
  StrategyIcon,
  UploadSimpleIcon,
  XIcon,
} from '@phosphor-icons/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { ProductPickerSheet } from '@/components/create/v2/ProductPickerSheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useAuth } from '@/hooks/use-auth';
import { usePlanDocuments } from '@/hooks/usePlanDocuments';
import { useProjectProductReferences } from '@/hooks/useProjectProductReferences';
import { ALLOWED_FILE_MIMES, formatBytes, validateFile } from '@/lib/ai-agent/file-validation';
import { formatWhen } from '@/lib/format-when';
import { hasMaterializedProjectStrategy } from '@/lib/projects/project-strategy';
import {
  ICON_BUTTON,
  ITEM_META,
  ITEM_TITLE,
  MOBILE_HIT_AREA,
  PANEL_DIVIDER_INSET,
  ROW_HOVER,
  SECTION_TITLE,
  THUMBNAIL_SQUARE,
} from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';
import {
  type CampaignDocument,
  campaignDocumentService,
} from '@/services/data/CampaignDocumentService';
import type { CampaignWithCoverage } from '@/services/data/CampaignService';
import { documentsService } from '@/services/data/DocumentsService';
import { useDocumentDialogStore } from '@/stores/useDocumentDialogStore';
import { useStrategyDialogStore } from '@/stores/useStrategyDialogStore';

interface PlanContextSectionProps {
  campaign: CampaignWithCoverage;
  variant?: 'rail' | 'sheet';
  onClose?: () => void;
}

function iconForMime(mime: string) {
  return mime === 'application/pdf' ? FilePdfIcon : FileTextIcon;
}

/**
 * The single durable-reference surface for a project.
 *
 * Every row here already has a real persisted context path: strategy on the
 * project, generated documents through plan_documents, uploaded references
 * through campaign_documents, and Shopify products through project_product_refs.
 * Keeping them in one list mirrors the user's mental model without weakening
 * any of those source-specific storage or attribution contracts.
 */
export function PlanContextSection({
  campaign,
  variant = 'rail',
  onClose,
}: PlanContextSectionProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { session } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragDepthRef = useRef(0);
  const [isDragging, setIsDragging] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [activeDocumentId, setActiveDocumentId] = useState<string | null>(null);

  const {
    data: products = [],
    isLoading: productsLoading,
    isError: productsError,
    refetch: refetchProducts,
    replace: replaceProducts,
  } = useProjectProductReferences(campaign.id);

  const {
    documents,
    isLoading: documentsLoading,
    error: documentsError,
    unassign,
    isUnassigning,
  } = usePlanDocuments(campaign.id);

  const {
    data: referenceFiles = [],
    isLoading: filesLoading,
    isError: filesError,
  } = useQuery({
    queryKey: ['campaign-documents', campaign.id] as const,
    queryFn: () => campaignDocumentService.listForCampaign(campaign.id),
    staleTime: 30_000,
  });

  const activeDocument = activeDocumentId
    ? documents.find((document) => document.messageId === activeDocumentId)
    : null;
  const userId = session?.user?.id ?? null;
  const { data: activeMarkdown } = useQuery({
    queryKey: ['plan-documents-markdown', activeDocumentId, userId],
    queryFn: async () => {
      if (!activeDocumentId || !userId) return null;
      const record = await documentsService.getDocument(activeDocumentId, userId);
      return record?.markdown ?? '';
    },
    enabled: Boolean(activeDocumentId && userId),
    staleTime: 30_000,
  });

  useEffect(() => {
    if (!activeDocument || activeMarkdown === undefined) return;
    useDocumentDialogStore.getState().open({
      document: {
        id: activeDocument.id,
        title: activeDocument.title,
        markdown: activeMarkdown ?? '',
        type: activeDocument.type,
        wordCount: activeDocument.wordCount,
        sessionId: activeDocument.sessionId,
        messageId: activeDocument.messageId,
        updatedAt: activeDocument.assignedAt,
      },
      onOpenInChat: () => {
        setActiveDocumentId(null);
        navigate(
          `/?resumeSession=${activeDocument.sessionId}&messageId=${activeDocument.messageId}`,
        );
      },
      onClose: () => setActiveDocumentId(null),
    });
  }, [activeDocument, activeMarkdown, navigate]);

  const replaceProductIds = useCallback(
    async (ids: string[]) => {
      try {
        await replaceProducts.mutateAsync(ids);
      } catch {
        toast.error('Could not update project products. Try again.');
      }
    },
    [replaceProducts],
  );

  const uploadFiles = useCallback(
    async (files: File[]) => {
      for (const file of files) {
        const validation = validateFile({ type: file.type, size: file.size, name: file.name });
        if (!validation.ok) {
          toast.error(validation.reason ?? `${file.name} rejected`);
          continue;
        }
        try {
          await campaignDocumentService.upload(campaign.id, file);
          toast.success(`${file.name} added`);
        } catch (error: unknown) {
          toast.error(`Couldn't upload: ${error instanceof Error ? error.message : String(error)}`);
        }
      }
      await queryClient.invalidateQueries({
        queryKey: ['campaign-documents', campaign.id],
      });
    },
    [campaign.id, queryClient],
  );

  const removeReferenceFile = useCallback(
    async (file: CampaignDocument) => {
      try {
        await campaignDocumentService.remove(file.id);
        await queryClient.invalidateQueries({
          queryKey: ['campaign-documents', campaign.id],
        });
        toast.success(`Removed ${file.file_name}`);
      } catch (error: unknown) {
        toast.error(`Couldn't remove: ${error instanceof Error ? error.message : String(error)}`);
      }
    },
    [campaign.id, queryClient],
  );

  const onDragEnter = useCallback((event: React.DragEvent<HTMLElement>) => {
    if (!event.dataTransfer.types.includes('Files')) return;
    event.preventDefault();
    dragDepthRef.current += 1;
    setIsDragging(true);
  }, []);
  const onDragOver = useCallback((event: React.DragEvent<HTMLElement>) => {
    if (!event.dataTransfer.types.includes('Files')) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
  }, []);
  const onDragLeave = useCallback((event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setIsDragging(false);
  }, []);
  const onDrop = useCallback(
    (event: React.DragEvent<HTMLElement>) => {
      event.preventDefault();
      dragDepthRef.current = 0;
      setIsDragging(false);
      const files = Array.from(event.dataTransfer.files ?? []);
      if (files.length > 0) void uploadFiles(files);
    },
    [uploadFiles],
  );

  const strategy = campaign.strategy;
  const strategySummary = strategy as { name?: string; schedule?: unknown[] } | null;
  const hasStrategy = hasMaterializedProjectStrategy(strategy);
  const isLoading = productsLoading || documentsLoading || filesLoading;
  const isEmpty =
    !hasStrategy && documents.length === 0 && referenceFiles.length === 0 && products.length === 0;

  const addContextMenu = (
    <Tooltip>
      <DropdownMenu>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className={cn(
                ICON_BUTTON,
                MOBILE_HIT_AREA,
                variant === 'sheet' &&
                  'flex size-8 items-center justify-center justify-self-end rounded-full',
              )}
              aria-label="Add context"
            >
              <PlusIcon className="size-3.5" />
            </button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <DropdownMenuContent align="end" sideOffset={6} className="w-56">
          <DropdownMenuItem
            onSelect={() => setPickerOpen(true)}
            className="gap-2 text-caption"
            aria-label="Add product"
          >
            <ShoppingBagIcon className="size-4 text-muted-foreground/70" weight="regular" />
            <span>Add product</span>
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() => fileInputRef.current?.click()}
            className="gap-2 text-caption"
            aria-label="Upload reference file"
          >
            <UploadSimpleIcon className="size-4 text-muted-foreground/70" weight="regular" />
            <span>Upload reference file</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <TooltipContent side="bottom">Add context</TooltipContent>
    </Tooltip>
  );

  return (
    <>
      {variant === 'rail' && <Separator className={PANEL_DIVIDER_INSET} />}
      <section
        className={cn('transition-colors duration-200', isDragging && 'bg-skill/[0.04]')}
        aria-label="Project context"
        onDragEnter={onDragEnter}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        <div
          className={cn(
            'flex items-center',
            variant === 'sheet'
              ? 'border-b border-border/40 px-4 py-3'
              : 'justify-between px-5 pb-2 pt-4',
          )}
        >
          {variant === 'sheet' && (
            <button
              type="button"
              className={cn(
                ICON_BUTTON,
                MOBILE_HIT_AREA,
                'flex size-8 items-center justify-center rounded-full',
              )}
              aria-label="Close context"
              onClick={onClose}
            >
              <XIcon className="size-4" weight="bold" />
            </button>
          )}
          <h3 className={cn(SECTION_TITLE, variant === 'sheet' && 'flex-1 text-center')}>
            Context
          </h3>
          {addContextMenu}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ALLOWED_FILE_MIMES.join(',')}
          className="sr-only"
          tabIndex={-1}
          aria-hidden="true"
          onChange={(event) => {
            const files = Array.from(event.target.files ?? []);
            event.target.value = '';
            if (files.length > 0) void uploadFiles(files);
          }}
        />

        {(productsError || filesError || documentsError) && (
          <div
            className="mx-5 mb-2 flex items-center justify-between gap-3 rounded-md bg-muted/40 px-2.5 py-2 text-detail text-muted-foreground"
            role="alert"
          >
            <span>Some context couldn’t load.</span>
            <button
              type="button"
              className={cn(ICON_BUTTON, 'w-auto px-2 text-detail')}
              onClick={() => {
                void refetchProducts();
                void queryClient.invalidateQueries({
                  queryKey: ['campaign-documents', campaign.id],
                });
                void queryClient.invalidateQueries({
                  queryKey: ['plan-documents', campaign.id],
                });
              }}
            >
              Retry
            </button>
          </div>
        )}

        {isLoading ? (
          <div className="mx-5 mb-4 h-9 animate-pulse rounded-md bg-muted/40" />
        ) : isEmpty ? (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="mx-5 mb-4 flex w-[calc(100%-2.5rem)] items-center gap-3 rounded-md px-2 py-2 text-left transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label="Upload reference file"
          >
            <span className={THUMBNAIL_SQUARE}>
              <PlusIcon className="size-3.5 text-muted-foreground/60" />
            </span>
            <span className="min-w-0">
              <span className={ITEM_TITLE}>Add project context</span>
              <span className={cn(ITEM_META, 'block')}>Products, plans, or reference files</span>
            </span>
          </button>
        ) : (
          <ul className="pb-2">
            {hasStrategy && (
              <li>
                <button
                  type="button"
                  aria-label={`Open ${strategySummary?.name?.trim() || `${campaign.name} plan`}`}
                  className={cn(
                    'flex w-full items-center gap-3 px-5 py-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    ROW_HOVER,
                  )}
                  onClick={() => {
                    if (!strategy) return;
                    onClose?.();
                    useStrategyDialogStore.getState().open({
                      strategy: {
                        projectId: campaign.id,
                        projectName: campaign.name,
                        strategy,
                        updatedAt: campaign.updatedAt ?? undefined,
                      },
                    });
                  }}
                >
                  <span className={THUMBNAIL_SQUARE}>
                    <StrategyIcon className="size-3.5 text-muted-foreground/65" weight="regular" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className={cn(ITEM_TITLE, 'block truncate')}>
                      {strategySummary?.name?.trim() || `${campaign.name} plan`}
                    </span>
                    <span className={cn(ITEM_META, 'block')}>
                      Content plan
                      {Array.isArray(strategySummary?.schedule)
                        ? ` · ${strategySummary.schedule.length} ${strategySummary.schedule.length === 1 ? 'slot' : 'slots'}`
                        : ''}
                    </span>
                  </span>
                </button>
              </li>
            )}

            {documents.map((document) => {
              const DocumentIcon = document.type === 'blog' ? NewspaperClippingIcon : FileTextIcon;
              return (
                <li
                  key={document.id}
                  className={cn('group flex items-center gap-3 px-5 py-2', ROW_HOVER)}
                >
                  <button
                    type="button"
                    className="flex min-w-0 flex-1 items-center gap-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    onClick={() => setActiveDocumentId(document.messageId)}
                  >
                    <span className={THUMBNAIL_SQUARE}>
                      <DocumentIcon
                        className="size-3.5 text-muted-foreground/65"
                        weight="regular"
                      />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className={cn(ITEM_TITLE, 'block truncate')}>{document.title}</span>
                      <span className={cn(ITEM_META, 'block')}>
                        Generated document · {document.wordCount.toLocaleString()} words ·{' '}
                        {formatWhen(document.assignedAt)}
                      </span>
                    </span>
                  </button>
                  <button
                    type="button"
                    disabled={isUnassigning}
                    onClick={() => void unassign(document.id)}
                    aria-label={`Remove ${document.title}`}
                    className={cn(
                      ICON_BUTTON,
                      'opacity-100 md:opacity-0 md:group-hover:opacity-100',
                    )}
                  >
                    <XIcon className="size-3.5" />
                  </button>
                </li>
              );
            })}

            {referenceFiles.map((file) => {
              const FileIcon = iconForMime(file.mime_type);
              return (
                <li
                  key={file.id}
                  className={cn('group flex items-center gap-3 px-5 py-2', ROW_HOVER)}
                >
                  <span className={THUMBNAIL_SQUARE}>
                    <FileIcon className="size-3.5 text-muted-foreground/65" weight="regular" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className={cn(ITEM_TITLE, 'truncate')}>{file.file_name}</p>
                    <p className={ITEM_META}>
                      Reference file · {formatBytes(file.size_bytes)}
                      {file.extraction_status === 'pending' && ' · extracting'}
                      {file.extraction_status === 'failed' && ' · extract failed'}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => void removeReferenceFile(file)}
                    aria-label={`Remove ${file.file_name}`}
                    className={cn(
                      ICON_BUTTON,
                      'opacity-100 md:opacity-0 md:group-hover:opacity-100',
                    )}
                  >
                    <XIcon className="size-3.5" />
                  </button>
                </li>
              );
            })}

            {products.map((product) => (
              <li
                key={product.productId}
                className={cn('group flex items-center gap-3 px-5 py-2', ROW_HOVER)}
              >
                {product.imageUrl ? (
                  <img
                    src={product.imageUrl}
                    alt=""
                    className="size-7 shrink-0 rounded-md bg-muted object-cover"
                  />
                ) : (
                  <span className={THUMBNAIL_SQUARE}>
                    <ShoppingBagIcon
                      className="size-3.5 text-muted-foreground/65"
                      weight="regular"
                    />
                  </span>
                )}
                <div className="min-w-0 flex-1">
                  <p className={cn(ITEM_TITLE, 'truncate')}>{product.title}</p>
                  <p className={ITEM_META}>Product · Included in new chats</p>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    void replaceProductIds(
                      products
                        .filter((candidate) => candidate.productId !== product.productId)
                        .map((candidate) => candidate.productId),
                    )
                  }
                  aria-label={`Remove ${product.title}`}
                  className={cn(ICON_BUTTON, 'opacity-100 md:opacity-0 md:group-hover:opacity-100')}
                >
                  <XIcon className="size-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <ProductPickerSheet
        isOpen={pickerOpen}
        onClose={() => setPickerOpen(false)}
        selectedProductIds={products.map((product) => product.productId)}
        allowCollections={false}
        onSelectProduct={(product) => {
          if (product.type !== 'product') return;
          void replaceProductIds(
            products.some((existing) => existing.productId === product.id)
              ? products
                  .filter((existing) => existing.productId !== product.id)
                  .map((existing) => existing.productId)
              : [...products.map((existing) => existing.productId), product.id],
          );
        }}
      />
    </>
  );
}
